import {
    Repeater,
    RepeaterOptions,
    PagerOptions,
    PagedResults,
    repeaterDefaults,
} from './repeater';
import { merge, flattenDeep } from 'lodash-es';
import h from 'virtual-dom/h';
import createElement from 'virtual-dom/create-element';
import { normalizedStringTokens } from './string-helper';

export interface ListviewOptions {
    listViewId: string;
    rootElementId: string;
    localStorageContextKey?: string;
    wrapperElement?: (items: (string | VirtualDOM.VNode)[]) => VirtualDOM.VNode;
    tableProps?: VirtualDOM.createProperties;
    renderHeader?: boolean;
    getItems: (
        pageNumber?: number,
        pageSize?: number,
        searchStrings?: Record<string, string>,
        sortOrders?: [string, 'asc' | 'desc'][],
    ) => Promise<any[] | PagedResults>;
    preProcessItems?: (items: any[]) => any[];
    filterId?: string | string[];
    useDefaultFilter?: boolean;
    persistFilter?: boolean;
    customFilter?: (items: any[]) => any[];
    defaultOrder?: [string, 'asc' | 'desc'][];
    singleOrder?: boolean;
    customOrder?: (items: any[]) => any[];
    noResults?: () => string | VirtualDOM.VNode;
    rowProps?: (item: any) => VirtualDOM.createProperties;
    postRender?: (items: any[]) => void;
    pagerOptions?: PagerOptions;
    columns: ListviewColumn[];
}

export interface ListviewColumn {
    property?: string;
    header?: string | VirtualDOM.VNode;
    headerProps?: VirtualDOM.createProperties;
    position: number;
    props?: VirtualDOM.createProperties;
    sortable?: boolean;
    filterable?: boolean;
    filterString?: (value: any) => string | string[];
    renderHeader?: (column: ListviewColumn) => VirtualDOM.VNode;
    render?: (item: any) => string | VirtualDOM.VNode;
    actions?: ListviewAction[];
    type?: string;
    format?: any;
    show?: () => boolean;
    sortProperty?: string;
}

export interface ListviewAction {
    name: string;
    position: number;
    render: (item: any) => VirtualDOM.VNode;
    show?: (item: any) => boolean;
    type?: 'default' | 'confirm-overlay' | 'confirm-popup';
    overlayProps?: VirtualDOM.createProperties;
    popupProps?: VirtualDOM.createProperties;
    confirmTitle?: string;
    confirmText?: string | ((item: any) => VirtualDOM.VNode);
    confirmButtonProps?: VirtualDOM.createProperties;
    confirmButtonContent?: string | VirtualDOM.VNode[];
    cancelButtonProps?: VirtualDOM.createProperties;
    cancelButtonContent?: string | VirtualDOM.VNode[];
    cancelHeaderButtonProps?: VirtualDOM.createProperties;
    cancelHeaderButtonContent?: string | VirtualDOM.VNode[];
    onConfirm?: (item: any) => Promise<void>;
    onCancel?: (item: any) => Promise<void>;
}

export let listviewDefaults: any = merge({}, repeaterDefaults, {
    renderHeader: true,
    singleOrder: true,
});

export let listviewColumnDefaults: any = {
    header: '',
    sortable: true,
    filterable: true,
    type: 'default',
    show: () => true,
};

export let listviewColumnActionDefaults: any = {
    show: () => true,
    type: 'default',
    confirmText: 'Are you you want to do this?',
    confirmButtonContent: 'Yes',
    cancelButtonContent: 'No',
};

export let listviewRenderTypeDefaults: any = {
    string: (value: string) => {
        return value;
    },
    number: (value: number) => {
        return value.toString();
    },
    currency: (value: number) => {
        return value.toFixed(2);
    },
    datetime: (value: string | Date) => {
        value = typeof value === 'string' ? new Date(value) : value;

        return value.toString();
    },
    boolean: (value: boolean) => {
        return value.toString();
    },
    default: (value: any) => {
        return value ? value.toString() : value;
    },
};

export class Listview extends Repeater {
    public lvOptions: ListviewOptions; // The default options combined with inputted options
    public columns: ListviewColumn[]; // The columns of the listview table
    public actions: ListviewAction[]; // The actions of each listview item

    /**
     * Merges the given options with default options
     * Initializes the repeater
     * Sets the lvOptions, and initializes the columns
     */
    constructor(options: ListviewOptions) {
        let lvOptions = merge({}, listviewDefaults, options);
        super(<RepeaterOptions>lvOptions);
        this.lvOptions = lvOptions;
        this.initColumns();
    }

    /**
     * Default repeater options for setting the wrapper element
     * and rendering an item
     */
    protected initExtendedOptions(): any {
        let options = {
            renderItem: this.renderItem,
        };

        return options;
    }

    /**
     * Merges all columns and actions within the columns with the default values
     * and orders them based on position
     */
    protected initColumns(columns?: ListviewColumn[]): void {
        const effectiveColumns = columns ?? this.lvOptions.columns;

        this.columns = effectiveColumns
            .filter((c) => (c.show != null ? c.show() : true))
            .map((column) => ({
                ...listviewColumnDefaults,
                ...column,
                actions: (column.actions ?? [])
                    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                    .map((action) => ({
                        ...listviewColumnActionDefaults,
                        ...action,
                    })),
            }))
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }

    /**
     * Reinit the columns and loads the data
     */
    public reinitColumns(columns: ListviewColumn[]): void {
        this.initColumns(columns);
        this.load();
    }

    /**
     * Generates a simple table element
     */
    protected wrapperElement(
        elements: string[] | VirtualDOM.VNode[],
    ): VirtualDOM.VNode {
        let props = merge({}, { className: '' }, this.lvOptions.tableProps);
        props.className += ' listview';
        let root = h('table', props, elements);
        return root;
    }

    /**
     * Helper to retrieve nested properties
     * Example: 'user.name' => item.user.name
     */
    protected getNestedProperty(property: string, obj: any): any {
        return property
            .split('.')
            .reduce((current, prop) => current?.[prop], obj);
    }

    /**
     * Overrides the default filter functionality of the repeater
     * Only filters on columns that are filterable and not action columns
     * All columns are mapped to strings with filterString (if exists)
     * or listviewRenderTypeDefaults (if type is set)
     * or just toString if type is string or number
     * Searches through all values for filterValue
     */
    protected defaultFilter(items: any[]): any[] {
        if (this.filters) {
            for (let filter of this.filters) {
                const searchWords = normalizedStringTokens(filter.value);

                items = items.filter((item) => {
                    let values = this.columns
                        .filter(
                            (c) =>
                                c.filterable &&
                                (!c.actions || c.actions.length === 0) &&
                                c.property,
                        )
                        .map((c) => {
                            let value = c.property ? item[c.property] : '';
                            if (c.filterString) {
                                value = c.filterString(value);
                            } else if (c.type) {
                                value = listviewRenderTypeDefaults[c.type](
                                    value,
                                    c.format,
                                );
                            }

                            return value;
                        });
                    values = flattenDeep(values);
                    values = values
                        .filter(
                            (value) =>
                                typeof value === 'string' ||
                                typeof value === 'number',
                        )
                        .map((value) =>
                            normalizedStringTokens(value.toString(), ' '),
                        );

                    values = flattenDeep(values);

                    return searchWords.every((searchWord) =>
                        values.some((value) => value.includes(searchWord)),
                    );
                });
            }
        }

        return items;
    }

    /**
     * Renders the items based on the column settings
     * Maps each column to an item property or to actions if defined
     * For actions, their render function is called if they should be showed
     * Otherwise render function of column (if defined) is called
     * or the listviewRenderTypeDefaults is called (if type is defined)
     * Returns a tr with tds for each column
     */
    protected renderItem(item: any): VirtualDOM.VNode {
        let renderedColumns = this.columns.map((c) => {
            let props = c.props || {};

            if (c.actions && c.actions.length > 0) {
                let actions = c.actions
                    .filter((a) => {
                        return a.show ? a.show(item) : true;
                    })
                    .map((a) => {
                        return this.renderAction(a, item);
                    });

                return h('td', props, actions);
            } else {
                let value = c.property
                    ? this.getNestedProperty(c.property, item)
                    : '';
                if (c.render) {
                    value = c.render(item);
                } else if (c.type) {
                    value = listviewRenderTypeDefaults[c.type](value, c.format);
                }

                return h('td', props, value);
            }
        });

        let rowProps = this.lvOptions.rowProps
            ? this.lvOptions.rowProps(item)
            : {};
        let jsonItem = JSON.stringify(item);
        rowProps = merge({}, { dataset: { listviewItem: jsonItem } }, rowProps);
        let row = h('tr', rowProps, renderedColumns);
        return row;
    }

    /**
     * Calls the render of the action
     * If action is confirm-overlay type, it will override the onclick of the element
     */
    protected renderAction(
        action: ListviewAction,
        item: any,
    ): VirtualDOM.VNode {
        let element = action.render(item);
        if (action.type === 'confirm-overlay') {
            element.properties.onclick = (event: Event) => {
                event.preventDefault();
                let element = <Element>event.target;
                this.renderConfirmOverlay(action, item, element);
            };
        } else if (action.type === 'confirm-popup') {
            element.properties.onclick = (event: Event) => {
                event.preventDefault();
                this.renderConfirmPopup(action, item);
            };
        }
        return element;
    }

    /**
     * Renders the confirm overlay with the settings of action
     * Searches for closest row of the clicked item
     * Gets the top, left, width, and height of the row
     * And places a div over that row
     * Adds a confirm text, cancel button, and confirm button on the overlay
     */
    protected renderConfirmOverlay(
        action: ListviewAction,
        item: any,
        element: Element,
    ): void {
        if (
            action.confirmText &&
            action.confirmButtonContent &&
            action.cancelButtonContent
        ) {
            let row = <HTMLElement>element.closest('tr');
            let position = row.getBoundingClientRect();

            let props: VirtualDOM.createProperties = merge(
                {},
                { className: '', style: {} },
                action.overlayProps,
            );
            props.className += ' confirm-overlay';
            props.style.top = `${row.offsetTop}px`;
            props.style.left = `${row.offsetLeft}px`;
            props.style.width = `${row.offsetWidth}px`;
            props.style.height = `${row.offsetHeight}px`;

            let confirmText =
                typeof action.confirmText === 'function'
                    ? action.confirmText(item)
                    : h('span', action.confirmText);

            let confirmButtonProps = action.confirmButtonProps || {};
            confirmButtonProps.className += ' confirm-button';
            confirmButtonProps.onclick = (event: Event) => {
                let target = <Element>event.target;
                target = <Element>target.closest('.confirm-overlay');

                if (action.onConfirm) {
                    action
                        .onConfirm(item)
                        .then(() => this.removeConfirmOverlay(target));
                } else {
                    this.removeConfirmOverlay(target);
                }
            };
            let confirmButton = h(
                'button',
                confirmButtonProps,
                action.confirmButtonContent,
            );

            let cancelButtonProps = action.cancelButtonProps || {};
            cancelButtonProps.className += ' cancel-button';
            cancelButtonProps.onclick = (event: Event) => {
                let target = <Element>event.target;
                target = <Element>target.closest('.confirm-overlay');

                if (action.onCancel) {
                    action
                        .onCancel(item)
                        .then(() => this.removeConfirmOverlay(target));
                } else {
                    this.removeConfirmOverlay(target);
                }
            };
            let cancelButton = h(
                'button',
                cancelButtonProps,
                action.cancelButtonContent,
            );

            let overlayVirtual = h('div', props, [
                confirmText,
                confirmButton,
                cancelButton,
            ]);
            let overlay = createElement(overlayVirtual);
            this.rootDomElement.appendChild(overlay);
        }
    }

    /**
     * Removes the confirm overlay
     */
    protected removeConfirmOverlay(overlay: Element): void {
        let parent = <Element | null>overlay.parentNode;
        if (parent) {
            parent.removeChild(overlay);
        }
    }

    protected renderConfirmPopup(action: ListviewAction, item: any): void {
        let title = h('h3', action.confirmTitle!);
        let cancelHeaderButtonProps = action.cancelHeaderButtonProps || {};
        cancelHeaderButtonProps.className += ' cancel-button';
        cancelHeaderButtonProps.onclick = (event: Event) => {
            this.cancelPopup(event, item, action);
        };
        let cancelHeaderButton = h(
            'a',
            { href: '#', className: 'panel-close' },
            [
                h(
                    'span',
                    cancelHeaderButtonProps,
                    action.cancelHeaderButtonContent || '',
                ),
            ],
        );

        let header = h('div', { className: 'panel-header' }, [
            title,
            cancelHeaderButton,
        ]);

        let confirmText =
            typeof action.confirmText === 'function'
                ? action.confirmText(item)
                : h('p', action.confirmText ?? '');

        let confirmButtonProps = action.confirmButtonProps || {};
        confirmButtonProps.className += ' confirm-button';
        confirmButtonProps.onclick = (event: Event) => {
            let target = <Element>event.target;
            let wrapper = <Element>target.closest('.listview-popup-wrapper');

            if (action.onConfirm) {
                action
                    .onConfirm(item)
                    .then(() => this.removeConfirmPopup(wrapper));
            } else {
                this.removeConfirmPopup(wrapper);
            }
        };
        let confirmButton = h(
            'button',
            confirmButtonProps,
            action.confirmButtonContent || '',
        );

        let cancelButtonProps = action.cancelButtonProps || {};
        cancelButtonProps.className += ' cancel-button';
        cancelButtonProps.onclick = (event: Event) => {
            this.cancelPopup(event, item, action);
        };
        let cancelButton = h(
            'button',
            cancelButtonProps,
            action.cancelButtonContent || '',
        );

        let body = h('div', { className: 'panel-body' }, [
            confirmText,
            confirmButton,
            cancelButton,
        ]);

        let backface = h(
            'div',
            {
                className: 'backface listview-backface loaded',
                onclick: (event: Event) => {
                    this.cancelPopup(event, item, action);
                },
            },
            '',
        );

        let props: VirtualDOM.createProperties = merge(
            {},
            { className: '', style: {} },
            action.popupProps,
        );
        props.className += ' popup listview-popup loaded';
        let popup = h('div', props, [
            h('div', { className: 'panel' }, [header, body]),
        ]);

        let virtualWrapper = h('div', { className: 'listview-popup-wrapper' }, [
            popup,
            backface,
        ]);
        let wrapper = createElement(virtualWrapper);
        this.rootDomElement.appendChild(wrapper);
        setTimeout(() => {
            document.documentElement!.classList.add('popup-open');
            document
                .getElementsByClassName('listview-popup')[0]
                .classList.add('open');
            document
                .getElementsByClassName('listview-backface')[0]
                .classList.add('open');
        }, 50);
    }

    protected cancelPopup(
        event: Event,
        item: any,
        action: ListviewAction,
    ): void {
        let target = <Element>event.target;
        let wrapper = <Element>target.closest('.listview-popup-wrapper');

        if (action.onCancel) {
            action.onCancel(item).then(() => this.removeConfirmPopup(wrapper));
        } else {
            this.removeConfirmPopup(wrapper);
        }
    }

    protected removeConfirmPopup(wrapper: Element): void {
        document.documentElement!.classList.remove('popup-open');
        document
            .getElementsByClassName('listview-popup')[0]
            .classList.remove('open');
        document
            .getElementsByClassName('listview-backface')[0]
            .classList.remove('open');

        let parent = <Element | null>wrapper.parentNode;
        if (parent) {
            setTimeout(() => parent!.removeChild(wrapper), 250);
        }
    }

    /**
     * Renders the header and body of the listview table
     */
    protected renderData(items: any[]): VirtualDOM.VNode[] {
        let elements: VirtualDOM.VNode[] = [];

        if (this.lvOptions.renderHeader) {
            elements.push(this.renderTableHeader());
        }

        elements.push(this.renderTableBody(items));
        return elements;
    }

    /**
     * Renders the header of the listview table
     * Maps columns to virtual-dom elements
     * Calls custom renderHeader if present
     * Otherwise will call default render function
     * Wraps all columns in a thead and tr
     */
    protected renderTableHeader(): VirtualDOM.VNode {
        let columns = this.columns.map((c) => {
            if (c.renderHeader) {
                return c.renderHeader(c);
            }

            return this.defaultColumnHeader(c);
        });

        let header = h('thead', [h('tr', columns)]);
        return header;
    }

    /**
     * Default function to render a column header
     * If column is sortable, add appropriate classes and events
     * Adds an a around header name for clicking
     */
    protected defaultColumnHeader(column: ListviewColumn): VirtualDOM.VNode {
        let headerOptions: VirtualDOM.createProperties = merge(
            {},
            { className: '' },
            column.headerProps,
        );
        if (
            (column.sortable === undefined || column.sortable === true) &&
            (column.sortProperty ?? column.property)
        ) {
            const currentSortOrder = this.sortOrders.find((so) => {
                return so[0] === (column.sortProperty ?? column.property);
            });

            if (currentSortOrder) {
                headerOptions.className += ` ${currentSortOrder[1]}`;
            }

            headerOptions.onclick = (event: any) => {
                event.preventDefault();
                this.toggleColumnSort(column);
            };
        }
        let headerText = column.header || '';
        let link =
            column.sortable === false
                ? headerText
                : h('a', { href: '#' }, [headerText]);
        let header = h('th', headerOptions, [link]);
        return header;
    }

    /**
     * Toggles the sort order of a column
     * Will from no ordering -> asc -> desc -> no ordering
     * When singleOrder is true, it will only set the new ordering and removes the old ones
     * Will rebuild after toggling
     */
    protected toggleColumnSort(column: ListviewColumn): void {
        if (column.property) {
            let columnSort = this.sortOrders.find((so) => {
                return so[0] === (column.sortProperty ?? column.property);
            });

            if (columnSort) {
                if (columnSort[1] === 'asc') {
                    columnSort[1] = 'desc';
                } else {
                    this.sortOrders = this.sortOrders.filter((so) => {
                        return (
                            so[0] !== (column.sortProperty ?? column.property)
                        );
                    });
                }
            } else {
                if (this.lvOptions.singleOrder) {
                    this.sortOrders = [
                        [column.sortProperty ?? column.property, 'asc'],
                    ];
                } else {
                    this.sortOrders.push([
                        column.sortProperty ?? column.property,
                        'asc',
                    ]);
                }
            }

            this.localStorageManager.setNestedItem(
                'sortOrders',
                this.sortOrders,
            );
        }

        if (this.options.pagerOptions?.serverSidePaging) {
            this.load();
        } else {
            this.build();
        }
    }

    /**
     * Renders the body of the listview table
     * Maps items to virtual dom elements via the renderItem function
     * or renders noResults if no items
     * Wraps all items in tbody
     */
    protected renderTableBody(items: any[]): VirtualDOM.VNode {
        let rows =
            items.length > 0
                ? items.map((item) => this.renderItem(item))
                : [this.renderNoResults()];
        let body = h('tbody', rows);
        return body;
    }

    /**
     * Renders if no items are present
     * Renders single row with centered text
     */
    protected renderNoResults(): VirtualDOM.VNode {
        let noResults = this.options.noResults
            ? this.options.noResults()
            : 'No results';

        let column = h(
            'td',
            {
                colSpan: this.lvOptions.columns.length,
                style: {
                    textAlign: 'center',
                },
            },
            [noResults],
        );
        let row = h('tr', [column]);
        return row;
    }
}
